import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import ReasonConfigForm from 'pages/Settings/ReasonConfig/ReasonConfigForm';

import { ENUMS, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

const OnHoldForm = NiceModal.create(({ otoOrderIDs, orderIDs, status, onHoldReason, onHoldComment }) => {
	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					onHoldReason: yup.string().nullable().required(),
					status: yup.string(),
					comments: yup.string().nullable()
				})
				.required(),
		[]
	);

	const modal = useModal();
	const { showError, showSuccess } = useAlert();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: { status, onHoldReason, comments: onHoldComment }
	});

	const { handleSubmit, setValue, watch } = formMethods;

	useEffect(() => {
		// we will show the user orderIDs but we will send otoOrderIDs to the backend
		let orderNumbers = '';
		orderIDs.forEach((orderID, index) => {
			orderNumbers += orderID + (index !== orderIDs.length - 1 ? ', ' : '');
		});
		setValue('orderNumbers', orderNumbers);
	}, [orderIDs]);

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(ORDER_ACTIONS.UPDATE_ON_HOLD_REASON, {
				otoOrderIDs,
				status,
				onHoldReason: data.onHoldReason,
				comments: data.comments
			});

			if (response.status === 200) {
				showSuccess();
				dispatch(updateDynamicListsInPage());
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			if (error.response?.data.code === 'error.O022') {
				showError({
					message: 'error.orderActionNotPermittedForUser'
				});
			} else if (error.response?.data.code === 'error.O021') {
				showError({
					message: 'error.orderUpdateNotAllowed'
				});
			} else {
				showError();
			}
		}
		modal.remove();
		setIsLoading(false);
	};

	const defaultColSpan = {
		md: 6
	};
	return (
		<Modal maxWidth="medium" fullwidth className="modal-onHold">
			<ModalTitle>
				<Typography variant="p1">onHoldOrder</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="status" colSpan={defaultColSpan}>
						<Status name="status" value={status} />
					</FormItem>

					<FormItem label="orderNumbers" colSpan={defaultColSpan}>
						<StringInput name="orderNumbers" disabled={true} />
					</FormItem>

					<FormItem label="onHoldReason" required colSpan={defaultColSpan}>
						<EnumSelect
							name="onHoldReason"
							enumName={ENUMS.ON_HOLD_REASON_CONFIG}
							placeholder="selectOne"
							value={watch('onHoldReason')}
							setValue={(value) => {
								setValue('onHoldReason', value, { shouldValidate: true, shouldDirty: true });
							}}
							firstItem={{
								action: () =>
									NiceModal.show(ReasonConfigForm, {
										setReasonConfig: (value) => {
											setValue(`onHoldReason`, value, {
												shouldValidate: true,
												shouldDirty: true
											});
										},
										reasonType: 'onHold'
									}),
								label: <Typography color="var(--blue)">addANewReason</Typography>
							}}
						/>
					</FormItem>

					<FormItem label="comments">
						<TextArea name="comments" placeholder="" />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						NiceModal.show(ConfirmationModal, {
							yesAction: () => {
								modal.remove();
							}
						});
					}}
					variant="outline"
				/>
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faFloppyDisk}
					label="save"
					loading={isLoading}
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default OnHoldForm;
